import ActionButton from "@/components/buttons/ActionButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import { resolveFormValidationError, selectFilterFunction } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { AutoComplete, Button, Card, Checkbox, Flex, Form, Input, Modal } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useCreateCalcDraft,
  useRealtyGenerateOffer,
  useRealtySendOffer,
  useTravelGenerateOffer,
  useTravelSendOffer,
  useUpdateCalcDraft,
  useVehicleGenerateOffer,
  useVehicleSendOffer,
} from "../../../../common/api/queries";
import { QueryKeys } from "../../../../common/constants";
import { ContactType } from "../../../../common/enums";
import { Contact } from "../../../../common/types";
import { CreateUpdateCalcDraft, RealtyCalcDraft, TravelCalcDraft, VehicleCalcDraft } from "../../drafts/types";
import { OfferType } from "../enums";
import { OperationStage } from "../realty/enums";
import type { RealtyCalc, RealtyFormClients } from "../realty/types";
import type { TravelCalc } from "../travel/types";
import { CalcResult, Offer } from "../types";
import {
  isRealtyCalcData,
  isRealtyOffer,
  isTravelCalcData,
  isTravelOffer,
  isVehicleCalcData,
  isVehicleOffer,
} from "../utils";
import type { VehicleCalc, VehicleFormClients } from "../vehicle/types";
import { resolveVehicleOfferType, resolveVehiclePolicyHolder } from "../vehicle/utils";

type Props = {
  isUpdate: boolean;
  formType?: "createUpdateDraft" | "createOffer" | "sendOffer";
  calcData: TravelCalc | RealtyCalc | VehicleCalc;
  calcResults: CalcResult[];
  operationStage: OperationStage;
  draft?: TravelCalcDraft | VehicleCalcDraft | RealtyCalcDraft;
  onFinish: VoidFunction;
  clients?: RealtyFormClients | VehicleFormClients;
};

export const DraftOfferForm = ({
  isUpdate,
  formType,
  draft,
  onFinish,
  calcData,
  calcResults,
  operationStage,
  clients,
}: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<Offer<TravelCalc | RealtyCalc | VehicleCalc>>();

  const createCalcDraft = useCreateCalcDraft();
  const updateCalcDraft = useUpdateCalcDraft();
  const vehicleSendOffer = useVehicleSendOffer();
  const realtySendOffer = useRealtySendOffer();
  const travelSendOffer = useTravelSendOffer();
  const vehicleGenerateOffer = useVehicleGenerateOffer();
  const realtyGenerateOffer = useRealtyGenerateOffer();
  const travelGenerateOffer = useTravelGenerateOffer();

  const getDefaultOfferType = () => {
    if (isTravelCalcData(calcData)) {
      return OfferType.TRAVEL;
    } else if (isRealtyCalcData(calcData)) {
      return OfferType.REALTY;
    } else {
      return resolveVehicleOfferType(calcData.type);
    }
  };

  const getInitialClientName = () => {
    if (formType === "createUpdateDraft" && isUpdate) {
      return draft?.clientName;
    }

    return isRealtyCalcData(calcData)
      ? clients?.policyHolder?.aggregatedName
      : (clients as VehicleFormClients)?.holder?.aggregatedName;
  };

  useEffect(() => {
    if (formType) {
      form.setFieldsValue({
        clientName: getInitialClientName(),
        calcRequest: calcData,
        calcResponse: { results: calcResults },
        type: getDefaultOfferType(),
      });
    }
  }, [formType, isUpdate]);

  const processDraftCreateOrUpdate = (values: Offer<TravelCalc | RealtyCalc | VehicleCalc>) => {
    const newDraft: CreateUpdateCalcDraft = {
      draftData: calcData,
      calcResponse: { results: calcResults },
      clientName: values.clientName ?? "",
      calcType: calcData.type,
      stage: operationStage,
    };

    if (draft && isUpdate) {
      updateCalcDraft.mutate({
        id: draft.id,
        object: { ...newDraft, optimisticLockVersion: draft.optimisticLockVersion },
      });
    } else {
      createCalcDraft.mutate(newDraft, {
        onSuccess: (draft) =>
          navigate(
            {
              search: URLQuery.searchParamsToString({
                searchParams: {
                  [QueryKeys.DRAFT_ID]: draft.id,
                  [QueryKeys.RECORD_ID]: undefined,
                  [QueryKeys.CONTRACT_ID]: undefined,
                },
                append: true,
              }),
            },
            { replace: true },
          ),
      });
    }
  };

  const processCreateOffer = (values: Offer<TravelCalc | RealtyCalc | VehicleCalc>) => {
    if (isTravelOffer(values)) {
      travelGenerateOffer.mutate(values);
    } else if (isRealtyOffer(values)) {
      realtyGenerateOffer.mutate(values);
    } else if (isVehicleOffer(values)) {
      vehicleGenerateOffer.mutate(values);
    }
  };

  const processSendOffer = (values: Offer<TravelCalc | RealtyCalc | VehicleCalc>) => {
    if (isTravelOffer(values)) {
      travelSendOffer.mutate(values);
    } else if (isRealtyOffer(values)) {
      realtySendOffer.mutate(values);
    } else if (isVehicleOffer(values)) {
      vehicleSendOffer.mutate(values);
    }
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (formType === "createOffer") {
          processCreateOffer(values);
        } else if (formType === "createUpdateDraft") {
          processDraftCreateOrUpdate(values);
        } else {
          processSendOffer(values);
        }

        onFinish();
      })
      .catch(resolveFormValidationError);
  };

  const getTitle = () => {
    if (formType === "createOffer") {
      return t("calc.titles.offerCreate");
    } else if (formType === "createUpdateDraft") {
      return t("calc.titles.draftCreate");
    } else {
      return t("calc.titles.offerSend");
    }
  };

  const getOkText = () => {
    if (formType === "createOffer") {
      return t("calc.actions.generateOffer");
    } else if (formType === "createUpdateDraft") {
      return t("calc.actions.saveDraft");
    } else {
      return t("calc.actions.sendOffer");
    }
  };

  const handleGenerateRiskOffer = (event: CheckboxChangeEvent) => {
    if (event.target.checked) {
      form.setFieldValue("type", OfferType.REALTY_RISKS);
    } else {
      form.setFieldValue("type", OfferType.REALTY);
    }
  };

  const getAutocompleteEmailOptions = () => {
    let contacts: Contact[] = [];

    if (isRealtyCalcData(calcData) && clients?.policyHolder?.contacts) {
      contacts = clients.policyHolder.contacts;
    } else if (isVehicleCalcData(calcData) && clients) {
      contacts = resolveVehiclePolicyHolder(clients, calcData.clientsData.policyHolderRelation)?.contacts ?? [];
    }

    return [
      ...new Set(
        contacts
          .filter((c) => (c.type === ContactType.EMAIL || c.type === ContactType.MAIN_EMAIL) && !!c.value?.trim())
          .map((c) => c.value),
      ),
    ].map((email) => ({ value: email, label: email }));
  };

  return (
    <Modal
      title={getTitle()}
      open={isDefined(formType)}
      okText={getOkText()}
      cancelText={t("common.close")}
      centered
      onOk={handleFormSubmit}
      onCancel={onFinish}
      afterClose={() => form.resetFields()}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="draftOfferForm">
        <HiddenInput name="calcRequest" />
        <HiddenInput name="calcResponse" />
        <HiddenInput name="type" />

        <Form.Item
          name="clientName"
          label={t("calc.helpers.clientName")}
          rules={[validations.notBlank, validations.size(1, 255)]}
          initialValue={getInitialClientName()}
        >
          <Input />
        </Form.Item>

        {formType === "sendOffer" ? (
          <Form.List name="emails" initialValue={[""]}>
            {(fields, { add, remove }) => (
              <Card
                style={{ marginBottom: 16 }}
                size="small"
                title={t("common.toWho")}
                extra={
                  <ActionButton type="text" icon="plus" size="small" variant="outlined" onClick={() => add("")}>
                    {t("calc.actions.addEmail")}
                  </ActionButton>
                }
              >
                {fields.map(({ key, ...field }) => (
                  <Flex key={field.name}>
                    <Form.Item
                      {...field}
                      style={{ flex: 1 }}
                      rules={[
                        validations.notBlank,
                        validations.size(1, 254),
                        validations.email,
                        validations.noRepeatedValue("emails"),
                      ]}
                    >
                      {field.name === 0 && clients ? (
                        <AutoComplete<string>
                          popupMatchSelectWidth={false}
                          placeholder="email@email.com"
                          options={getAutocompleteEmailOptions()}
                          showSearch={{ filterOption: selectFilterFunction }}
                        />
                      ) : (
                        <Input placeholder="email@email.com" />
                      )}
                    </Form.Item>

                    {field.name !== 0 ? (
                      <Button
                        style={{ marginLeft: 8 }}
                        type="text"
                        onClick={() => remove(field.name)}
                        icon={<AntIcon type="close" />}
                      />
                    ) : undefined}
                  </Flex>
                ))}
              </Card>
            )}
          </Form.List>
        ) : undefined}

        {formType === "sendOffer" ? (
          <Form.Item name="sendToLoggedUser" initialValue={false} valuePropName="checked" noStyle>
            <Checkbox style={{ marginBottom: 16 }}>{t("calc.actions.sendToMe")}</Checkbox>
          </Form.Item>
        ) : undefined}

        {formType !== "createUpdateDraft" && isRealtyCalcData(calcData) ? (
          <div>
            <Checkbox onChange={handleGenerateRiskOffer}>
              {formType === "sendOffer"
                ? t("calc.realty.actions.sendRisksOffer")
                : t("calc.realty.actions.saveRisksOffer")}
            </Checkbox>
          </div>
        ) : undefined}
      </Form>
    </Modal>
  );
};
